import React, { useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { stageLabel, KIND_LABELS, fmtDate } from "./shared";
import LeadDrawer from "./LeadDrawer";
import { DOC_TYPE_LABELS, docStatus, STATUS_META, DOC_CATALOG } from "@/components/cadastre/docCatalog";
import { Eye, Users, Calendar, ChevronDown, ChevronUp, Search, FileText, Circle, Home as HomeIcon, Trash2, Pencil } from "lucide-react";

const TYPE_LABELS = {
    appartamento: "Appartamento", villa: "Villa", attico: "Attico", loft: "Loft",
    casa: "Casa", cantina: "Cantina", uffici: "Uffici",
    locali_commerciali: "Locali commerciali", box_auto: "Box auto",
};

export default function CrmProperties() {
    const [props, setProps] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [insights, setInsights] = useState({});
    const [openLead, setOpenLead] = useState(null);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [cadMatchIds, setCadMatchIds] = useState(null);

    useEffect(() => {
        Promise.all([api.get("/me/properties"), api.get("/crm/agents").catch(() => ({ data: { agents: [] } }))])
            .then(([p, a]) => { setProps(p.data.items || []); setAgents(a.data.agents || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const q = query.trim();
        if (!q) { setCadMatchIds(null); return; }
        const t = setTimeout(() => {
            api.get("/crm/search-cadastre", { params: { q } }).then((r) => setCadMatchIds(r.data.property_ids || [])).catch(() => setCadMatchIds([]));
        }, 400);
        return () => clearTimeout(t);
    }, [query]);

    const toggle = async (id) => {
        if (openId === id) { setOpenId(null); return; }
        setOpenId(id);
        if (!insights[id]) {
            try {
                const [r, m] = await Promise.all([
                    api.get(`/crm/property/${id}/insights`),
                    api.get(`/crm/property/${id}/matching-leads`).catch(() => ({ data: { items: [] } })),
                ]);
                setInsights((x) => ({ ...x, [id]: { ...r.data, matches: m.data.items || [] } }));
            } catch (e) { console.error(e); }
        }
    };

    const deleteProperty = async (id) => {
        if (!window.confirm("Sei sicuro? L'annuncio verrà eliminato in modo permanente dal tuo portafoglio e dal database.")) return;
        try {
            await api.delete(`/properties/${id}`);
            setProps((cur) => cur.filter((p) => p.id !== id));
            if (openId === id) setOpenId(null);
            toast.success("Immobile eliminato");
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione"); }
    };

    if (loading) return <div className="p-8 text-center text-ink-400 text-sm">Caricamento immobili...</div>;

    const q = query.trim().toLowerCase();
    const visibleProps = props.filter((p) => {
        if (!q) return true;
        const textMatch = (p.title || "").toLowerCase().includes(q) || (p.city || "").toLowerCase().includes(q);
        const cadMatch = cadMatchIds ? cadMatchIds.includes(p.id) : false;
        return textMatch || cadMatch;
    });

    return (
        <div data-testid="crm-properties-tab">
            <p className="text-xs text-ink-400 mb-4">Ogni immobile è collegato al CRM: clienti interessati, visite, documentazione catastale e storico attività.</p>
            <div className="relative mb-5 max-w-xl">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input data-testid="crm-prop-search" value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Cerca per titolo, città, foglio, particella, subalterno, categoria, comune, indirizzo catastale..."
                    className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none" />
            </div>
            <div className="space-y-4">
                {visibleProps.map((p) => {
                    const open = openId === p.id;
                    const ins = insights[p.id];
                    return (
                        <div key={p.id} className="vd-card vd-lead-row overflow-hidden">
                            <button data-testid={`crm-prop-${p.id}`} onClick={() => toggle(p.id)}
                                className="w-full flex items-stretch gap-0 text-left text-sm hover:bg-white/5 transition-colors">
                                {/* Thumbnail */}
                                <div data-testid={`crm-prop-thumb-${p.id}`} className="w-24 h-24 md:w-32 md:h-24 flex-shrink-0 border-r border-white/10 bg-black/50 relative overflow-hidden">
                                    {p.photos && p.photos.length > 0 ? (
                                        <img src={p.photos[0]} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                                    ) : (
                                        <div data-testid={`crm-prop-thumb-empty-${p.id}`} className="w-full h-full flex flex-col items-center justify-center text-gold/50">
                                            <HomeIcon size={22} />
                                            <span className="text-[8px] uppercase tracking-wider mt-1 text-ink-500">No foto</span>
                                        </div>
                                    )}
                                </div>
                                {/* Info */}
                                <div className="flex-1 min-w-0 px-4 py-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                                    <div className="md:col-span-5 min-w-0">
                                        <div className="text-ink-50 font-serif truncate flex items-center gap-2">
                                            {p.title}
                                            {p.owner_reference && <span data-testid={`crm-prop-owner-${p.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-gold/10 text-gold border border-gold/30 shrink-0">{p.owner_reference}</span>}
                                        </div>
                                        <div className="text-[11px] text-ink-400 truncate mt-0.5">
                                            {TYPE_LABELS[p.property_type] || p.property_type || "—"} · {p.transaction_type === "rent" || p.transaction_type === "affitto" ? "Affitto" : "Vendita"}
                                        </div>
                                        <div className="text-[10px] text-ink-500 truncate mt-0.5 flex items-center gap-1"><span className="text-gold">◈</span> {p.address ? `${p.address}, ${p.city}` : p.city || "—"}</div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="text-price font-serif">{formatPrice(p.price)}</div>
                                        <div className="text-[10px] text-ink-500 uppercase tracking-wider mt-0.5">{p.surface_sqm ? `${p.surface_sqm} m²` : "— m²"}</div>
                                    </div>
                                    <div className="md:col-span-2 text-xs text-ink-400 flex items-center gap-1.5"><Eye size={12} />{p.views || 0} views</div>
                                    <div className="md:col-span-2">
                                        <span className={`text-[10px] uppercase px-2 py-0.5 ${p.status === "sold" || p.status === "rented" ? "bg-green-900/30 text-green-300" : p.status === "removed" ? "bg-red-900/30 text-red-300" : "bg-ink-900 text-ink-400 border border-white/10"}`}>
                                            {p.status || "active"}
                                        </span>
                                    </div>
                                    <div className="md:col-span-1 text-right text-ink-400 flex items-center justify-end gap-2">
                                        <Link to={`/annunci/${p.id}/modifica`} onClick={(e) => e.stopPropagation()} data-testid={`crm-prop-edit-${p.id}`}
                                            title="Modifica immobile" className="p-1 text-ink-500 hover:text-gold"><Pencil size={14} /></Link>
                                        <button data-testid={`crm-prop-delete-${p.id}`} onClick={(e) => { e.stopPropagation(); deleteProperty(p.id); }}
                                            title="Elimina immobile" className="p-1 text-ink-500 hover:text-red-300"><Trash2 size={14} /></button>
                                        {open ? <ChevronUp size={15} className="inline" /> : <ChevronDown size={15} className="inline" />}
                                    </div>
                                </div>
                            </button>
                            {open && (
                                <div data-testid={`crm-prop-insights-${p.id}`} className="border-t border-white/10 px-4 py-5 grid grid-cols-1 lg:grid-cols-4 gap-6">
                                    {!ins && <div className="text-xs text-ink-400 col-span-4">Caricamento dati...</div>}
                                    {ins && (
                                        <>
                                            <div>
                                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-400 mb-3"><Users size={12} /> Clienti interessati ({(ins.leads || []).length})</div>
                                                {(ins.leads || []).length === 0 && <div className="text-xs text-ink-500">Nessun lead collegato.</div>}
                                                {(ins.leads || []).slice(0, 8).map((l) => (
                                                    <button key={l.id} onClick={() => setOpenLead(l.id)} className="w-full flex items-center justify-between py-1.5 text-xs border-b border-white/5 hover:text-gold text-left">
                                                        <span className="text-ink-100 truncate">{l.name}{l.type === "client" && <span className="ml-1.5 text-[8px] uppercase px-1 bg-green-900/30 text-green-300">C</span>}</span>
                                                        <span className="text-ink-400 text-[10px]">{stageLabel(l.stage)}</span>
                                                    </button>
                                                ))}
                                                {(ins.matches || []).length > 0 && (
                                                    <div data-testid={`prop-matches-${p.id}`} className="mt-3">
                                                        <div className="text-[10px] uppercase tracking-wider text-gold mb-1.5">🎯 Compatibili per ricerca ({(ins.matches || []).length})</div>
                                                        {(ins.matches || []).slice(0, 6).map((m) => (
                                                            <button key={m.lead_id} onClick={() => setOpenLead(m.lead_id)} className="w-full flex items-center justify-between py-1.5 text-xs border-b border-white/5 hover:text-gold text-left">
                                                                <span className="text-ink-100 truncate">{m.name}</span>
                                                                <span className={`text-[10px] px-1.5 py-0.5 border shrink-0 ${m.score >= 80 ? "border-green-700/40 text-green-300" : "border-gold/40 text-gold"}`}>{m.score}%</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-400 mb-3"><Calendar size={12} /> Visite ({(ins.appointments || []).length})</div>
                                                {(ins.appointments || []).length === 0 && <div className="text-xs text-ink-500">Nessuna visita.</div>}
                                                {(ins.appointments || []).slice(0, 8).map((a) => (
                                                    <div key={a.id} className="flex items-center justify-between py-1.5 text-xs border-b border-white/5">
                                                        <span className="text-ink-100 truncate">{a.client_name}</span>
                                                        <span className="text-ink-400 text-[10px]">{fmtDate(a.start_at)}</span>
                                                    </div>
                                                ))}
                                                <Link to={`/annunci/${p.id}`} className="inline-block mt-3 text-[11px] text-gold hover:underline">→ Vedi annuncio pubblico</Link>
                                                <button data-testid={`prop-pdf-${p.id}`} onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${p.id}/brochure.pdf`, "_blank")}
                                                    className="block mt-2 text-[11px] text-gold hover:underline">📄 Scheda immobile PDF</button>
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-3">Storico attività</div>
                                                {(ins.activities || []).length === 0 && <div className="text-xs text-ink-500">Nessuna attività.</div>}
                                                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                                    {(ins.activities || []).slice(0, 12).map((a) => (
                                                        <div key={a.id} className="text-[11px]">
                                                            <span className="text-ink-400">{fmtDate(a.created_at)} · {KIND_LABELS[a.kind] || a.kind}</span>
                                                            <div className="text-ink-100 truncate">{a.text}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <DocPanel ins={ins} propId={p.id} />
                                        </>
                                    )}
                                    {ins && (
                                        <div className="lg:col-span-4 pt-4 border-t border-white/10 flex justify-end gap-2">
                                            <Link to={`/annunci/${p.id}/modifica`} data-testid={`crm-prop-edit-detail-${p.id}`}
                                                className="feature-toggle-btn px-4 py-2 text-[11px] flex items-center gap-1.5 text-ink-200 hover:text-gold">
                                                <Pencil size={13} /> Modifica immobile
                                            </Link>
                                            <button data-testid={`crm-prop-delete-detail-${p.id}`} onClick={() => deleteProperty(p.id)}
                                                className="feature-toggle-btn px-4 py-2 text-[11px] flex items-center gap-1.5 text-red-300 hover:text-red-200">
                                                <Trash2 size={13} /> Elimina immobile
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {visibleProps.length === 0 && <div className="p-10 text-center text-ink-400 text-sm">{q ? "Nessun immobile corrisponde alla ricerca." : "Nessun immobile pubblicato."}</div>}
            </div>
            {openLead && <LeadDrawer leadId={openLead} agents={agents} onClose={() => setOpenLead(null)} onChanged={() => setInsights({})} />}
        </div>
    );
}

function DocPanel({ ins, propId }) {
    const comp = ins.completeness || { pct: 0, checklist: [] };
    const docs = ins.documents || [];
    const cad = ins.cadastre;
    const stale = docs.filter((d) => docStatus(d) === "da_aggiornare").length;
    const missing = comp.checklist.filter((c) => !c.done && ["planimetria", "visura", "ape", "catasto_base"].includes(c.key));
    const lastUpload = docs.length > 0 ? docs.map((d) => d.uploaded_at).sort().slice(-1)[0] : null;
    const color = comp.pct >= 80 ? "bg-green-500" : comp.pct >= 50 ? "bg-gold" : "bg-red-400";
    const coveredCats = DOC_CATALOG.map((c) => ({
        label: c.label,
        loaded: c.items.filter((it) => docs.some((d) => d.doc_type === it.id)).length,
        total: c.items.length,
    })).filter((c) => c.loaded > 0);
    return (
        <div data-testid={`doc-panel-${propId}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-400 mb-3"><FileText size={12} /> Documentazione</div>
            <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-ink-400">Completezza</span>
                <span data-testid={`doc-pct-${propId}`} className="font-serif text-lg text-ink-50">{comp.pct}%</span>
            </div>
            <div className="h-1.5 bg-ink-950 overflow-hidden mb-2">
                <div className={`h-full ${color}`} style={{ width: `${comp.pct}%` }} />
            </div>
            <div className="text-[11px] text-ink-300 mb-1">
                {docs.length} documenti caricati{stale > 0 && <span className="text-amber-300"> · {stale} da aggiornare</span>}
            </div>
            {lastUpload && <div className="text-[10px] text-ink-500 mb-1">Ultimo caricamento: {fmtDate(lastUpload)}</div>}
            {cad?.updated_at && <div className="text-[10px] text-ink-500 mb-2">Catasto aggiornato: {fmtDate(cad.updated_at)}</div>}
            {cad?.foglio && <div className="text-[11px] text-ink-300 mb-2">Fg. {cad.foglio}{cad.particella ? ` · Part. ${cad.particella}` : ""}{cad.subalterno ? ` · Sub. ${cad.subalterno}` : ""}{cad.categoria ? ` · ${cad.categoria}` : ""}</div>}
            {coveredCats.map((c) => (
                <div key={c.label} className="flex items-center justify-between text-[11px] py-1 border-b border-white/5">
                    <span className="text-ink-100 truncate">{c.label}</span>
                    <span className="text-ink-400 shrink-0 ml-2">{c.loaded}/{c.total}</span>
                </div>
            ))}
            {docs.slice(0, 4).map((d) => {
                const st = STATUS_META[docStatus(d)];
                return (
                    <div key={d.id} className="flex items-center gap-1.5 text-[11px] text-ink-100 py-1 border-b border-white/5">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${st.dot}`} />
                        <span className="truncate">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</span>
                    </div>
                );
            })}
            {missing.map((m) => (
                <div key={m.key} className="flex items-center gap-1.5 text-[11px] text-ink-500 py-1 border-b border-white/5">
                    <Circle size={11} className="shrink-0 text-red-400/60" />
                    <span className="truncate">Manca: {m.label}</span>
                </div>
            ))}
            <Link to={`/annunci/${propId}/modifica`} className="inline-block mt-3 text-[11px] text-gold hover:underline">→ Gestisci documenti</Link>
        </div>
    );
}
